// PdfDropzone — large, mobile-friendly file picker for PDF uploads.
// The native <input type="file"> renders as a tiny inline element on iOS,
// so we hide it visually and expose a big tappable card/label instead.
// Supports drag-and-drop on desktop.
import React, { useRef, useState, useCallback } from "react";

export default function PdfDropzone({ file, onFile, testid = "pdf-dropzone", disabled }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleFiles = useCallback((files) => {
    if (!files || !files.length) return;
    const f = files[0];
    if (!f) return;
    // Validate type — be lenient: some browsers omit MIME for .pdf
    const isPdf = (f.type && f.type.includes("pdf")) || /\.pdf$/i.test(f.name || "");
    if (!isPdf) {
      alert("الملف يجب أن يكون من نوع PDF");
      return;
    }
    onFile(f);
  }, [onFile]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer?.files);
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };

  const sizeKb = file ? Math.round(file.size / 1024) : 0;
  const sizeLbl = file ? (sizeKb > 1024 ? `${(sizeKb / 1024).toFixed(2)} MB` : `${sizeKb} KB`) : "";

  return (
    <div
      onClick={pick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") pick(); }}
      role="button"
      tabIndex={0}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      data-testid={testid}
      aria-disabled={disabled}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        border: `2px dashed ${dragging ? "var(--brown-deep)" : (file ? "var(--success)" : "var(--gold-deep)")}`,
        background: dragging ? "rgba(140, 95, 50, 0.06)" : (file ? "rgba(34, 197, 94, 0.06)" : "rgba(212, 175, 55, 0.05)"),
        borderRadius: 12,
        padding: "18px 14px",
        textAlign: "center",
        transition: "all 0.18s ease",
        opacity: disabled ? 0.55 : 1,
        userSelect: "none",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        data-testid={`${testid}-input`}
      />
      {file ? (
        <>
          <div style={{ fontSize: 30, marginBottom: 6 }}>📄</div>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)", wordBreak: "break-all", marginBottom: 4 }}>
            {file.name}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted)" }}>{sizeLbl} · جاهز للرفع</div>
          <div style={{ fontSize: 11, color: "var(--brown-deep)", marginTop: 6, textDecoration: "underline" }}>
            اضغط هنا لاختيار ملف آخر
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 36, marginBottom: 6 }}>📤</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
            اضغط لاختيار ملف PDF
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
            أو اسحب الملف وأفلته هنا · حد أقصى 25 ميجا
          </div>
        </>
      )}
    </div>
  );
}
